import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";

import IconButton from "@cayuse/shared-react.components.icon-button";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { isEmpty } from "lodash";

import { RESEARCH_TEAM } from "globals/constants/formObjects";

import { initialAssociation } from "./constants";
import { RTAssociationRow } from "./RTAssociationRow";
import styles from "./styles.css";

const { ASSOCIATION_TYPE } = RESEARCH_TEAM;

export const RTAssociations = forwardRef((props, ref) => {
	const {
		teamMembers,
		index,
		memberToAssociationLookupMap,
		selectedAssociationsDetails,
		isPresent,
		effortFormat,
		costShareEffort,
		sponsoredEffort,
		creditField,
		associationType,
		dictionary,
		displayConditions,
		updateValue,
		onChange,
		config,
		...rest
	} = props;

	const [ associations, setAssociations ] = useState([]);
	const [ memberAssociations, setMemberAssociations ] = useState([]);
	const memberId = teamMembers[index]?.member;
	const associationKey = associationType === ASSOCIATION_TYPE.EXTERNAL
		? "externalAssociations"
		: "associations";

	useImperativeHandle(ref, () => ({
		resetAssociations: () => {
			setAssociations([]);
			setMemberAssociations([]);
		}
	}));

	useEffect(() => {
		const mappedAssociations = (teamMembers[index][associationKey] ?? []).map(
			association => {
				const lookup =
                    (isPresent(memberId)
                    	? memberToAssociationLookupMap[memberId]?.find(
                    		item => item.id === association.association
                    	)
                    	: {}) ?? {};

				return {
					...association,
					lookup,
					details: selectedAssociationsDetails.find(
						item => item.id === association.association
					),
				};
			}
		);

		setAssociations(mappedAssociations);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ teamMembers[index][associationKey] ]);

	useEffect(() => {
		const memberToAssociation = isPresent(memberId)
			? memberToAssociationLookupMap[memberId]?.filter(
				association =>
					association.associationType === associationType
			)
			: [];
		setMemberAssociations(memberToAssociation);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ memberId, memberToAssociationLookupMap ]);

	const addAssociation = () => {
		const newAssociation = associationType === ASSOCIATION_TYPE.EXTERNAL
			? { association: null }
			: { ...initialAssociation };
		setAssociations(prevAssociations => ([ ...prevAssociations, newAssociation ]));
		if (associationType === ASSOCIATION_TYPE.EXTERNAL) {
			updateValue(onChange, value =>
				value[index].externalAssociations.push(newAssociation)
			);
		} else {
			updateValue(onChange, value =>
				value[index].associations.push(newAssociation)
			);
		}
	};

	const removeAssociation = subIndex => {
		const associationsKey = associationType === ASSOCIATION_TYPE.EXTERNAL ? "externalAssociations" : "associations";
		updateValue(onChange, value => value[index][associationsKey].splice(subIndex, 1));
		setAssociations(prevAssociations => {
			const updatedAssociations = [ ...prevAssociations ];
			updatedAssociations.splice(subIndex, 1);

			return updatedAssociations;
		});
	};

	const showWarningMsg = useCallback(item => {
		return (
			associationType === ASSOCIATION_TYPE.INTERNAL &&
            isNonEmptyString(item.association) &&
			displayConditions.shouldDisplayAtLeastOneEffortField &&
            (isEmpty(item.lookup) || !!item.lookup?.removed)
		);
	}, [ associationType, displayConditions.shouldDisplayAtLeastOneEffortField ]);

	return (
		<>
			{associations.map((association, subIndex) => (
				<>
					<RTAssociationRow
						key={`row-${index}-${subIndex}`}
						subIndex={subIndex}
						record={association}
						memberAssociations={memberAssociations}
						teamMembers={teamMembers}
						index={index}
						isPresent={isPresent}
						effortFormat={effortFormat}
						associationType={associationType}
						displayConditions={displayConditions}
						updateValue={updateValue}
						onChange={onChange}
						config={config}
						removeAssociation={removeAssociation}
						loukups={memberToAssociationLookupMap[memberId] || []}
						{...rest}
					/>
					{showWarningMsg(association) && (
						<div className={`${styles.footerErrorBox} ${styles.error}`}>
							{dictionary.errors?.internalAssociationHasBeenRemoved ??
                        "Effort fields are reference only. Team member is no longer affiliated with the internal association."}
						</div>
					)}
				</>
			))}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					backgroundColor: "var(--color-lighter-grey)",
				}}
			>
				<div className={styles.addInternalAssociation}>
					<IconButton
						icon="plus"
						disabled={!config.enabled}
						onClick={() => addAssociation(associationType)}
						labelPosition="left"
						secondary
						className={styles.lightBackground}
					>
						{associationType === ASSOCIATION_TYPE.INTERNAL
							? dictionary.addInternalAssociationButton ||
											  "Add Internal Association"
							: dictionary.addExternalAssociationButton ||
											  "Add External Association"}
					</IconButton>
				</div>
			</div>
		</>
	);
});
